/**
 * Edit Financial Information Modal
 *
 * Modal for editing customer financial information including:
 * - Agreed Terms (payment terms from API)
 * - Due In Days
 * - Hold Status
 * - Credit Limit
 * - Primary Currency
 * - Tax Information (tax type from API)
 *
 * Uses React Hook Form + payment terms/tax type API
 */

import { useMemo, useEffect } from 'react';
import { Modal, Input, Select, Row, Col, Typography, theme } from 'antd';
import { useForm, Controller, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { controlHeightTokens } from '@/styles/tokens';
import { useFormStyles } from '@/styles/formStyles';
import { usePaymentTerms } from '@/hooks/usePaymentTerms';
import { useTaxTypes } from '@/hooks/useTaxTypes';
import { useCurrencies } from '@/hooks/useCurrencies';
import { useErrorHandler } from '@/lib/errors/useErrorHandler';
import { useTranslation } from 'react-i18next';
import { HOLD_STATUS_OPTIONS } from '@/models/customer';
import type { CustomerPaymentResponse, HoldStatus } from '@/types/api/customer';
import { numberToHoldStatus } from '@/types/api/customer';

const { Text } = Typography;

// Schema for the form
// taxTypeId is required for creating/updating payment data
const editCustomerFinancialInfoSchema = z.object({
  depositPercentage: z.number().min(0).max(100).optional(),
  cashDiscountPercentage: z.number().min(0).max(100).optional(),
  basePaymentTermId: z.number().optional(),
  dueInDays: z.number().int().min(0).optional(),
  creditLimit: z.number().min(0).optional(),
  holdStatus: z.enum(['auto', 'on_hold', 'off_hold']),
  primaryCurrency: z.string().min(1, 'customer.validation.primaryCurrencyRequired'),
  taxTypeId: z.number({ required_error: 'customer.validation.taxTypeRequired' }),
  taxRate: z.number().optional(),
});

interface EditFinancialInfoFormData {
  depositPercentage?: number;
  cashDiscountPercentage?: number;
  basePaymentTermId?: number;
  dueInDays?: number;
  creditLimit?: number;
  holdStatus: HoldStatus;
  primaryCurrency: string;
  taxTypeId?: number; // Optional in interface, but required by zod schema for form validation
  taxRate?: number;
}

export interface EditFinancialInfoModalProps {
  open: boolean;
  onCancel: () => void;
  onSave: (data: EditFinancialInfoFormData) => Promise<void>;
  paymentData: CustomerPaymentResponse | null;
  countryId?: number;
  localTaxId?: string | null;
  loading?: boolean;
}

export const EditFinancialInfoModal: React.FC<EditFinancialInfoModalProps> = ({
  open,
  onCancel,
  onSave,
  paymentData,
  countryId,
  localTaxId,
  loading = false,
}) => {
  const { token } = theme.useToken();
  const formStyles = useFormStyles();
  const { t, i18n } = useTranslation('customers');
  const { handleError, handleSuccess } = useErrorHandler();

  // Fetch payment terms, tax types, and currencies (typeId: 2 = Customer)
  const { paymentTerms, isLoading: isLoadingPaymentTerms } = usePaymentTerms(2);
  const { taxTypes, isLoading: isLoadingTaxTypes } = useTaxTypes(countryId);
  const { currencyOptions, isLoading: isLoadingCurrencies } = useCurrencies();

  // Compute form values from payment data using useMemo (prevents unnecessary recalculations)
  const formValues = useMemo(() => {
    if (!paymentData) {
      return undefined;
    }
    return {
      depositPercentage: paymentData.depositPercentage ?? undefined,
      cashDiscountPercentage: paymentData.cashDiscountPercentage ?? undefined,
      // Use basePaymentTermId from paymentTerm object (new API structure)
      basePaymentTermId: paymentData.paymentTerm?.basePaymentTermId ?? undefined,
      dueInDays: paymentData.dueInDays ?? undefined,
      creditLimit: paymentData.creditLimit ?? undefined,
      // API returns holdStatus as number, convert to string enum
      holdStatus: numberToHoldStatus(paymentData.holdStatus),
      primaryCurrency: paymentData.primaryCurrency || 'USD',
      // Tax fields are now flat (not nested object)
      taxTypeId: paymentData.taxTypeId ?? undefined,
      taxRate: paymentData.taxRate ?? undefined,
    };
  }, [paymentData]);

  const {
    control,
    handleSubmit,
    setValue,
    reset,
    formState: { errors, touchedFields, isSubmitting },
  } = useForm<EditFinancialInfoFormData>({
    resolver: zodResolver(editCustomerFinancialInfoSchema),
    defaultValues: {
      depositPercentage: undefined,
      cashDiscountPercentage: undefined,
      basePaymentTermId: undefined,
      dueInDays: undefined,
      creditLimit: undefined,
      holdStatus: 'auto',
      primaryCurrency: 'USD',
      taxTypeId: undefined,
      taxRate: undefined,
    },
    // Use `values` prop for reactive updates from external data (recommended by RHF docs)
    values: formValues,
    mode: 'onTouched',
    reValidateMode: 'onChange',
  });

  // Reset form when modal opens to ensure fresh data from API
  useEffect(() => {
    if (open && formValues) {
      reset(formValues);
    }
  }, [open, formValues, reset]);

  // Use useWatch for better performance (doesn't cause full form re-render)
  const selectedTermId = useWatch({ control, name: 'basePaymentTermId' });
  const selectedTaxTypeId = useWatch({ control, name: 'taxTypeId' });

  // Auto-update tax rate when tax type changes (wrapped in useEffect to avoid setState during render)
  useEffect(() => {
    const selectedTaxType = taxTypes.find((t) => t.taxTypeId === selectedTaxTypeId);
    if (selectedTaxType) {
      setValue('taxRate', selectedTaxType.taxRate);
    }
  }, [selectedTaxTypeId, taxTypes, setValue]);

  // Auto-update term days when payment term changes (wrapped in useEffect to avoid setState during render)
  useEffect(() => {
    const term = paymentTerms.find((term) => term.paymentTermId === selectedTermId);
    if (term) {
      setValue('dueInDays', term.dueDays);
    }
  }, [selectedTermId, paymentTerms, setValue]);

  // Selected term for UI logic (isZeroDayTerm check)
  const selectedTerm = paymentTerms.find((term) => term.paymentTermId === selectedTermId);

  const handleFormSubmit = async (data: EditFinancialInfoFormData) => {
    try {
      await onSave(data);
      handleSuccess(t('customer.messages.financialInfoUpdatedSuccess'));
      onCancel();
    } catch (error) {
      handleError(error);
    }
  };

  // Override formStyles with generous spacing (matching EditBasicInfoModal)
  const fieldContainerStyle = {
    ...formStyles.fieldContainerStyle,
    marginBottom: '32px',
    padding: '20px',
  };

  // Use standardized styles from formStyles (matching EditBasicInfoModal pattern)
  const sectionTitleStyle = formStyles.sectionTitleStyle;
  const labelStyle = formStyles.labelStyle;
  const inputStyle = formStyles.inputStyle;

  // Payment terms options (from API)
  const paymentTermOptions = useMemo(() => {
    return paymentTerms.map((term) => ({
      label: i18n.language.startsWith('zh') ? term.termNameZh : term.termNameEn,
      value: term.paymentTermId,
    }));
  }, [paymentTerms, i18n.language]);

  // Hold status options (with i18n)
  const holdStatusOptions = useMemo(() => {
    return HOLD_STATUS_OPTIONS.map((status) => ({
      label: i18n.language.startsWith('zh') ? status.labelZh : status.label,
      value: status.value,
    }));
  }, [i18n.language]);

  // Tax type options (from API)
  const taxTypeOptions = useMemo(() => {
    return taxTypes.map((taxType) => ({
      label: `${taxType.taxType} (${taxType.taxRate}%)`,
      value: taxType.taxTypeId,
    }));
  }, [taxTypes]);

  // Currency options are now fetched from the API via useCurrencies hook

  // Check if selected payment term has 0 due days (like COD)
  const isZeroDayTerm = selectedTerm?.dueDays === 0;

  return (
    <>
      {/* Global styles for dropdown items to match input height */}
      <style>
        {`
          .ant-select-dropdown .ant-select-item {
            min-height: ${controlHeightTokens.controlHeightXL}px !important;
            padding: 10px 12px !important;
            font-size: ${token.fontSize}px !important;
            line-height: ${controlHeightTokens.controlHeightXL - 20}px !important;
            display: flex !important;
            align-items: center !important;
          }
        `}
      </style>
      <Modal
        title={t('customer.modals.editFinancialInformation')}
        open={open}
        onCancel={onCancel}
        onOk={handleSubmit(handleFormSubmit)}
        okText={t('customer.actions.save')}
        cancelText={t('customer.actions.cancel')}
        confirmLoading={isSubmitting || loading}
        width={700}
        okButtonProps={{
          style: { height: `${controlHeightTokens.controlHeightXL}px` },
        }}
        cancelButtonProps={{
          style: { height: `${controlHeightTokens.controlHeightXL}px` },
        }}
      >
        <form>
          {/* Payment Terms Section */}
          <div style={fieldContainerStyle}>
            <Text strong style={sectionTitleStyle}>
              {t('customer.sections.paymentTermsSection')}
            </Text>

            {/* Row 1: Deposit + Cash Discount */}
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <Text style={labelStyle}>{t('customer.labels.deposit')}</Text>
                <Controller
                  name="depositPercentage"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      size="large"
                      type="number"
                      min={0}
                      max={100}
                      placeholder={t('customer.placeholders.deposit')}
                      suffix="%"
                      allowClear
                      value={field.value ?? ''}
                      onChange={(e) => {
                        const value = e.target.value ? Number(e.target.value) : undefined;
                        field.onChange(value);
                      }}
                      style={inputStyle}
                    />
                  )}
                />
              </Col>
              <Col span={12}>
                <Text style={labelStyle}>{t('customer.labels.cashDiscount')}</Text>
                <Controller
                  name="cashDiscountPercentage"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      size="large"
                      type="number"
                      min={0}
                      max={100}
                      placeholder={t('customer.placeholders.cashDiscount')}
                      suffix="%"
                      allowClear
                      value={field.value ?? ''}
                      onChange={(e) => {
                        const value = e.target.value ? Number(e.target.value) : undefined;
                        field.onChange(value);
                      }}
                      style={inputStyle}
                    />
                  )}
                />
              </Col>
            </Row>

            {/* Row 2: Agreed Terms + Due In Days */}
            <Row gutter={[16, 16]} style={{ marginTop: '16px' }}>
              <Col span={12}>
                <Text style={labelStyle}>{t('customer.labels.terms')}</Text>
                <Controller
                  name="basePaymentTermId"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      size="large"
                      placeholder={t('customer.placeholders.agreedTerms')}
                      loading={isLoadingPaymentTerms}
                      options={paymentTermOptions}
                      allowClear
                      style={inputStyle}
                    />
                  )}
                />
              </Col>
              <Col span={12}>
                <Text style={labelStyle}>{t('customer.fields.dueInDays')}</Text>
                <Controller
                  name="dueInDays"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      size="large"
                      type="number"
                      placeholder={isZeroDayTerm ? t('customer.paymentTerms.notApplicable') : t('customer.placeholders.dueInDays')}
                      suffix={t('customer.misc.suffix.days')}
                      disabled={isZeroDayTerm}
                      value={isZeroDayTerm ? '' : (field.value ?? '')}
                      onChange={(e) => {
                        const value = e.target.value ? Number(e.target.value) : undefined;
                        field.onChange(value);
                      }}
                      style={{
                        ...inputStyle,
                        backgroundColor: isZeroDayTerm
                          ? token.colorBgContainerDisabled
                          : token.colorBgContainer,
                      }}
                    />
                  )}
                />
              </Col>
            </Row>

            {/* Row 3: Credit Limit + Hold Status */}
            <Row gutter={[16, 16]} style={{ marginTop: '16px' }}>
              <Col span={12}>
                <Text style={labelStyle}>{t('customer.fields.creditLimit')}</Text>
                <Controller
                  name="creditLimit"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      size="large"
                      type="number"
                      placeholder={t('customer.placeholders.creditLimit')}
                      prefix="$"
                      allowClear
                      value={field.value ?? ''}
                      onChange={(e) => {
                        const value = e.target.value ? Number(e.target.value) : undefined;
                        field.onChange(value);
                      }}
                      style={inputStyle}
                    />
                  )}
                />
              </Col>
              <Col span={12}>
                <Text style={labelStyle}>
                  <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
                  {t('customer.fields.holdStatus')}
                </Text>
                <Controller
                  name="holdStatus"
                  control={control}
                  render={({ field }) => (
                    <>
                      <Select
                        {...field}
                        size="large"
                        placeholder={t('customer.placeholders.hold')}
                        options={holdStatusOptions}
                        style={{
                          ...inputStyle,
                          borderColor: errors.holdStatus && touchedFields.holdStatus ? token.colorError : undefined,
                        }}
                      />
                      {errors.holdStatus && touchedFields.holdStatus && (
                        <Text style={{ fontSize: token.fontSizeSM, color: token.colorError }}>
                          {t(errors.holdStatus.message!)}
                        </Text>
                      )}
                    </>
                  )}
                />
              </Col>
            </Row>

            {/* Row 4: Primary Currency */}
            <Row gutter={[16, 16]} style={{ marginTop: '16px' }}>
              <Col span={24}>
                <Text style={labelStyle}>
                  <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
                  {t('customer.fields.primaryCurrency')}
                </Text>
                <Controller
                  name="primaryCurrency"
                  control={control}
                  render={({ field }) => (
                    <>
                      <Select
                        {...field}
                        size="large"
                        placeholder={t('customer.placeholders.currency')}
                        showSearch
                        loading={isLoadingCurrencies}
                        options={currencyOptions}
                        style={{
                          ...inputStyle,
                          borderColor: errors.primaryCurrency && touchedFields.primaryCurrency ? token.colorError : undefined,
                        }}
                      />
                      {errors.primaryCurrency && touchedFields.primaryCurrency && (
                        <Text style={{ fontSize: token.fontSizeSM, color: token.colorError }}>
                          {t(errors.primaryCurrency.message!)}
                        </Text>
                      )}
                    </>
                  )}
                />
              </Col>
            </Row>
          </div>

          {/* Tax Information Section */}
          <div style={fieldContainerStyle}>
            <Text strong style={sectionTitleStyle}>
              {t('customer.sections.taxSection')}
            </Text>

            {/* Row 1: ABN (Display only - from customer detail) */}
            <Row gutter={[16, 16]}>
              <Col span={24}>
                <Text style={labelStyle}>{t('customer.fields.abn')}</Text>
                <Input
                  size="large"
                  value={localTaxId || '-'}
                  readOnly
                  style={{
                    ...inputStyle,
                    backgroundColor: token.colorBgContainerDisabled,
                    color: token.colorText,
                    cursor: 'not-allowed',
                  }}
                />
              </Col>
            </Row>

            {/* Row 2: Tax Type + Tax Rate */}
            <Row gutter={[16, 16]} style={{ marginTop: '16px' }}>
              <Col span={12}>
                <Text style={labelStyle}>
                  <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
                  {t('customer.fields.taxType')}
                </Text>
                <Controller
                  name="taxTypeId"
                  control={control}
                  render={({ field }) => (
                    <>
                      <Select
                        {...field}
                        size="large"
                        placeholder={t('customer.placeholders.taxType')}
                        loading={isLoadingTaxTypes}
                        options={taxTypeOptions}
                        style={{
                          ...inputStyle,
                          borderColor: errors.taxTypeId && touchedFields.taxTypeId ? token.colorError : undefined,
                        }}
                        status={errors.taxTypeId ? 'error' : undefined}
                      />
                      {errors.taxTypeId && touchedFields.taxTypeId && (
                        <Text style={{ fontSize: token.fontSizeSM, color: token.colorError }}>
                          {t(errors.taxTypeId.message!)}
                        </Text>
                      )}
                    </>
                  )}
                />
              </Col>
              <Col span={12}>
                <Text style={labelStyle}>{t('customer.fields.taxRate')}</Text>
                <Controller
                  name="taxRate"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      size="large"
                      type="number"
                      value={field.value !== undefined ? field.value : ''}
                      onChange={(e) => {
                        const value = e.target.value ? Number(e.target.value) : undefined;
                        field.onChange(value);
                      }}
                      suffix="%"
                      readOnly
                      style={{
                        ...inputStyle,
                        backgroundColor: token.colorBgContainerDisabled,
                        color: token.colorText,
                        cursor: 'not-allowed',
                      }}
                    />
                  )}
                />
              </Col>
            </Row>
          </div>
        </form>
      </Modal>
    </>
  );
};

export default EditFinancialInfoModal;
